import React, {
  createContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { getClaims, logout, refreshToken } from 'shared/api/auth';
import { UnauthorizedError } from 'shared/lib/http-errors';

// Important to always get/set from window, not state, so different tabs can share session.
function setWindowAccessToken(accessToken, expiration) {
  window.localStorage.setItem('accessToken', accessToken);
  window.localStorage.setItem('accessTokenExpiration', expiration);
}

function accessTokenIsValid() {
  // Epoch ms: compare as numbers. A Date against the stored string compares
  // lexicographically and can report a valid token as expired.
  const expiration = Number(window.localStorage.getItem('accessTokenExpiration'));
  if (!expiration) return false;
  return Date.now() < expiration;
}

function getWindowAccessToken() {
  return window.localStorage.getItem('accessToken');
}

function clearWindowAccessToken() {
  window.localStorage.clear();
}

export const AuthContext = createContext({
  getAccessToken: () => '',
  isAdmin: false,
  isLoggedIn: false,
  logout: () => null,
  setAccessToken: () => {},
  user: null,
});

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export default function AuthProvider({ children }) {
  // Only allow one request for an access token to be inflight at once.
  // Important because a refresh token only allows one new token pair to be created.
  const accessTokenPromise = useRef(null);
  const [user, setUser] = useState(undefined);
  useEffect(() => setUser(getClaims(getWindowAccessToken())), []);

  // Make sure to keep all the dependencies in callbacks as minimal as possible.
  // All components that make requests assume these values are stable.
  // If they are unstable it will lead to infinite request loops.
  const setAccessToken = useCallback((newAccessToken) => {
    if (!newAccessToken) {
      clearWindowAccessToken();
      setUser(null);
      return {};
    }

    const newUser = getClaims(newAccessToken);
    setWindowAccessToken(newAccessToken, newUser.exp);
    setUser(newUser);
    return newUser;
  }, []);

  const getAccessToken = useCallback(() => {
    const currentToken = getWindowAccessToken();

    if (!currentToken) {
      setAccessToken(null);
      return Promise.resolve(null);
    }

    if (accessTokenIsValid()) {
      // the access token is still valid. return the current one.
      return Promise.resolve(currentToken);
    }

    // Use the existing promise if there is already a refresh request happening.
    // Attempting to refresh the same token multiple times at once will cause errors.
    if (!accessTokenPromise.current) {
      accessTokenPromise.current = refreshToken(currentToken);
    }

    return accessTokenPromise.current.then(({ accessToken: newAccessToken }) => {
      setAccessToken(newAccessToken);
      return newAccessToken;
    }).catch((error) => {
      if (error instanceof UnauthorizedError) {
        console.error('session renewal is unauthorized: logging out');
        setAccessToken(null);
      } else {
        console.error('failed to refresh token', error);
      }
      return Promise.resolve(null);
    }).finally(() => {
      accessTokenPromise.current = null;
    });
  }, [setAccessToken]);

  const clearUser = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const { id } = getClaims(accessToken);
        await logout(accessToken, id);
      }
    } catch (error) {
      console.error('failed to cleanup session', error);
    } finally {
      clearWindowAccessToken();
      setUser(null);
    }
  }, [getAccessToken]);

  const value = useMemo(() => ({
    getAccessToken,
    isAdmin: user && user.role === 'admin',
    isLoggedIn: !!user,
    logout: clearUser,
    setAccessToken,
    user: user || {},
  }), [clearUser, getAccessToken, setAccessToken, user]);
  // Don't render the children until the user has been set.
  return (
    <AuthContext.Provider value={value}>
      {user === undefined ? null : children}
    </AuthContext.Provider>
  );
}
